// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useState, useEffect, useRef } from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { useIntl } from 'react-intl';
import {
  withStyles,
} from '@material-ui/core';
import { get, findIndex, includes } from 'lodash';
import queryString from 'query-string';
import { shape, bool, func, arrayOf, string } from 'prop-types';

import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import EditContainer from '../../edit';
import {
  fetchAuthSchemeDetails,
  resetApp,
  deleteAuth,
} from '../../../actions/authenticationSettings';
import {
  getAuthSchemeDetails,
  getIsError,
  getErrors,
  getIsAuthSaveSuccess,
  getIsAuthUpdateSuccess,
  getIsDeleteSuccess,
  getDeleteErrors,
} from '../../../reducers/authenticationSettings';
import { getUserDetails, getSamlProxied } from '../../../reducers/portalConfig';
import {
  TabsContainer,
  TabPanel,
} from '../../../components';
import {
  AUTH_SETTINGS_LDAP_DETAILS_HELP,
  AUTH_SETTINGS_LDAP_ATTRIBUTE_MAPPING_HELP,
  SAML_OLD,
  SAML,
  LDAP,
  DEFAULT,
  JWT,
} from './label';
import Details from './Details';
import ProviderConfiguration from './ProviderConfiguration';
import AttributeMapping from './AttributeMapping';
import Configuration from './Configuration';
import styles from './styles';

const hasAttributeMapDisable = (authDetails, authParamName) => {
  if(authParamName === 'LDAP') {
    return !get(authDetails, 'ConfigurationParams.baseDN') &&
    !get(authDetails, 'ConfigurationParams.bindDN') &&
    !get(authDetails, 'ConfigurationParams.bindPassword') &&
    !get(authDetails, 'ConfigurationParams.hostname') &&
    !get(authDetails, 'ConfigurationParams.ldapQueryEnd') &&
    !get(authDetails, 'ConfigurationParams.ldapQueryStart') &&
    !get(authDetails, 'ConfigurationParams.port');
  }
  if(authParamName === 'SAMLOld') {
    return !get(authDetails, 'ConfigurationParams.acsUrl') &&
    !get(authDetails, 'ConfigurationParams.url') &&
    !get(authDetails, 'ConfigurationParams.samlBinding') &&
    !get(authDetails, 'ConfigurationParams.samlTokenAttribute') &&
    !get(authDetails, 'ConfigurationParams.samlTokenAttributeIn');
  }
  if(authParamName === 'SAML') {
    return !get(authDetails, 'ConfigurationParams.acsUrl') &&
    !get(authDetails, 'ConfigurationParams.issuerId') &&
    !get(authDetails, 'ConfigurationParams.url') &&
    !get(authDetails, 'ConfigurationParams.samlBinding') &&
    !get(authDetails, 'ConfigurationParams.samlTokenAttribute') &&
    !get(authDetails, 'ConfigurationParams.samlTokenAttributeIn') &&
    !get(authDetails, 'ConfigurationParams.serviceProviderId') &&
    !get(authDetails, 'ConfigurationParams.certificateFileName');
  }
  if(authParamName === 'JWT') {
    return !get(authDetails, 'ConfigurationParams.discoveryEndPoint');
  }
}

const hasProviderConfigFields = (authDetails, authParamName) => {
  if(authParamName === 'LDAP') {
    return get(authDetails, 'ConfigurationParams.baseDN') &&
    get(authDetails, 'ConfigurationParams.bindDN') &&
    get(authDetails, 'ConfigurationParams.bindPassword') &&
    get(authDetails, 'ConfigurationParams.hostname') &&
    get(authDetails, 'ConfigurationParams.ldapQueryEnd') &&
    get(authDetails, 'ConfigurationParams.ldapQueryStart') &&
    get(authDetails, 'ConfigurationParams.port');
  }
  if(authParamName === 'SAMLOld') {
    return get(authDetails, 'ConfigurationParams.acsUrl') &&
    get(authDetails, 'ConfigurationParams.url') &&
    get(authDetails, 'ConfigurationParams.samlBinding') &&
    get(authDetails, 'ConfigurationParams.samlTokenAttribute') &&
    get(authDetails, 'ConfigurationParams.samlTokenAttributeIn');
  }
  if(authParamName === 'SAML') {
    return get(authDetails, 'ConfigurationParams.acsUrl') &&
    get(authDetails, 'ConfigurationParams.issuerId') &&
    get(authDetails, 'ConfigurationParams.url') &&
    get(authDetails, 'ConfigurationParams.samlBinding') &&
    get(authDetails, 'ConfigurationParams.samlTokenAttribute') &&
    get(authDetails, 'ConfigurationParams.samlTokenAttributeIn') &&
    get(authDetails, 'ConfigurationParams.serviceProviderId') &&
    get(authDetails, 'ConfigurationParams.certificateFileName');
  }
  if(authParamName === 'JWT') {
    return (get(authDetails, 'ConfigurationParams.discoveryEndPoint') ? true : false);
  }
}

const hasDetailsFields = (authDetails) => {
  return (get(authDetails, 'name') && get(authDetails, 'description'));
}

const hasAttributeMappingFields = (authDetails, authParamName) => {
  const hasDefaultFields = get(authDetails, 'AttributeMappings.EMAIL_ATTRIBUTE') &&
    get(authDetails, 'AttributeMappings.FIRST_NAME_ATTRIBUTE') &&
    get(authDetails, 'AttributeMappings.LAST_NAME_ATTRIBUTE');

  if (get(authDetails, 'authznType') === 'IDP') {
    const hasAuthFields = get(authDetails, 'AttributeMappings.ORGANIZATION_ATTRIBUTE') &&
    get(authDetails, 'AttributeMappings.ROLE_ATTRIBUTE');
    const hasRoleFields = Object.keys(get(authDetails, 'RoleMappings')).length > 0;
    if(authParamName === 'SAMLOld' || authParamName === 'SAML' ||
      authParamName === 'JWT') {
      return hasDefaultFields && hasAuthFields &&
      get(authDetails, 'AttributeMappings.LOGIN_ATTRIBUTE') && hasRoleFields;
    }
    return hasDefaultFields && hasAuthFields && hasRoleFields;
  }
  return hasDefaultFields;
}

const Tabs = [{
  id: 'basic-details',
  tabId: 'basic-details-tab',
  label: getI18nFormattedMessage('label.auth.basic.details'),
  tabPanel: (props) => (<Details {...props} />),
  onCancel: () => { window.location.href = '/publish/settings/authentication'; },
  isHidden: ({ authParamName }) => authParamName === 'default',
},{
  id: 'provider-configuration',
  tabId: 'provider-configuration-tab',
  label: getI18nFormattedMessage('label.auth.provider.configuration'),
  tabPanel: (props) => (<ProviderConfiguration {...props} />),
  isDisabled: ({ authDetails }) => !hasDetailsFields(authDetails),
  onCancel: () => { window.location.href = '/publish/settings/authentication'; },
  isHidden: ({ authParamName }) => authParamName === 'default',
}, {
  id: 'attribute-mapping',
  tabId: 'attribute-mapping-tab',
  label: getI18nFormattedMessage('label.auth.attribute.mapping'),
  isDisabled: ({ authDetails, authParamName }) =>
    hasAttributeMapDisable(authDetails, authParamName),
  tabPanel: (props) => (<AttributeMapping {...props} />),
  onCancel: () => { window.location.href = '/publish/settings/authentication'; },
  isHidden: ({ authParamName }) => authParamName === 'default',
}, {
  id: 'configuration',
  tabId: 'configuration-tab',
  label: getI18nFormattedMessage('label.configuration'),
  tabPanel: (props) => (<Configuration {...props} />),
  onCancel: () => { window.location.href = '/publish/settings/authentication'; },
  isHidden: ({ authParamName }) => authParamName !== 'default',
}];

const getTabs = (tabProps) => Tabs
  .filter(({ isHidden }) => !(isHidden && isHidden(tabProps)))
  .map(tab => ({
    ...tab,
    disabled: tab.isDisabled && tab.isDisabled(tabProps),
  }));

export const getDefaultSelectedTabIndex = (tabs, match) => {
  const tabName = get(match, 'params.tabName');
  const index = findIndex(tabs, tab => (tab.id === tabName));
  // if none found default to 0
  return (index > 0 ? index : 0);
};

export const AuthEdit = (props) => {
  const {
    classes,
    userContext,
    authDetails,
    isError,
    match,
    authErrors,
    isLoading,
    isSaveSuccess,
    isUpdateSuccess,
    isOktaIdpFlag,
    pathName,
   } = props;

   const headerRef = useRef(null);
   const uuid = get(props, 'match.params.authUuid');

   const getHelpItems = (id) => {
    let helpItems = { title: '', descriptions: '', subItems: [] };
    switch (id) {
      case 'basic-details-tab':
        helpItems = AUTH_SETTINGS_LDAP_DETAILS_HELP;
        break;
      case 'attribute-mapping-tab':
        helpItems = AUTH_SETTINGS_LDAP_ATTRIBUTE_MAPPING_HELP;
        break;
      default:
    }
    return helpItems;
  };

  const intl = getI18n(useIntl());
  const queryParam = queryString.parse(props.location.search);
  const authParamName = get(queryParam, 'authType');
  const qParam = get(queryParam, 'authType') ? `?authType=${queryParam.authType}` : '';
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isCancelButton, setIsCancelButton] = useState(false);
  const [dialogSubmitText, setDialogSubmitText] = useState(getI18nFormattedMessage('label.dialog.unsaved.changes.exit'));
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [errors, setErrors] = useState([]);
  const [tabs, setTabs] = useState(getTabs({
    userContext, uuid, authDetails, authParamName }));
  const defaultIndex = getDefaultSelectedTabIndex(tabs, match);
  const [tabIndex, setTabIndex] = useState(defaultIndex);
  const [currentTab, setCurrentTab] = useState(tabs[defaultIndex].tabId);
  const [helpItems, setHelpItems] = useState(getHelpItems(tabs[defaultIndex].tabId));
  const [headerTitle, setHeaderTitle] = useState('');

  useEffect(() => {
    if (!userContext.portalAdmin) { props.push('/404'); }
    if(!includes([SAML,LDAP,JWT,SAML_OLD,DEFAULT], queryParam.authType)) {
      props.push('/404');
    }
    props.resetApp();
  }, [userContext]);

  useEffect(() => {
    if(uuid)
      props.fetchAuthSchemeDetails(uuid);
  }, [uuid]);

  const getHeaderTitle = () => {
    const name = get(queryParam, 'authType') === 'SAMLOld' ? 'SAML DEPRECATED' :
      get(queryParam, 'authType');
    if (uuid) {
      return `${intl.getI18nMessage('label.edit.authentication.scheme', { 'name' : name })}`;
    }
    return `${intl.getI18nMessage('label.add.authentication.scheme', { 'name' : name })}`;
  };

  useEffect(() => {
    setHeaderTitle(getHeaderTitle());
  }, [props.location.search]);

  useEffect(() => {
    const filterTabs = getTabs({
      userContext, uuid, authDetails, authParamName,
    });
    setTabs(filterTabs);
    setTabIndex(getDefaultSelectedTabIndex(filterTabs, match));
  }, [uuid, authDetails, userContext]);

  useEffect(() => {
    const { tabId } = tabs[tabIndex];
    setCurrentTab(tabId);
    setHelpItems(getHelpItems(tabId));
  }, [tabIndex, tabs]);

  const notifyMessages = (message, status) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
    setTimeout(() => {
      notifyMessages('','')
    }, 3000);
  };

  const setStateTabs = (value) => {
    setTabIndex(tabs.findIndex(({ tabId }) => (tabId === value)));
  };

  useEffect(() => {
    const defaultIndex = getDefaultSelectedTabIndex(tabs, match);
    const tabName = get(match, 'params.tabName');
    if(tabName === 'tags') {
      notifyMessages('', '');
    }
    setTabIndex(defaultIndex);
  },[match]);

  useEffect(() => {
    if (isError) {
      notifyMessages('', '');
      if (authErrors.length > 0) {
        setErrors(authErrors);
        window.scrollTo(0, headerRef.offsetTop);
      }
    }
  }, [isError, authErrors]);

  useEffect(() => {
    if(isSaveSuccess || isUpdateSuccess) {
      props.push('/publish/settings/authentication');
    }
  })

  const redirectTab = (value) => {
    setStateTabs(value);
    notifyMessages('', '');
    setErrors([]);
    const tIndex = tabs.findIndex(({ tabId }) => (tabId === value));
    const { id } = tabs[tIndex];
    const url = uuid ? `/publish/settings/authentication/edit/${uuid}/${id}${qParam}`:
    `/publish/settings/authentication/add/${id}${qParam}`
    props.push(url);
  };


  const onDialogClose = () => {
    setIsDialogOpen(false);
    setDialogSubmitText(getI18nFormattedMessage('label.dialog.unsaved.changes.exit'));
  };

  const onDialogSubmit = () => {
    localStorage.setItem('isUnSavedChanges', false);
    setDialogSubmitText(getI18nFormattedMessage('label.dialog.unsaved.changes.exit'));
    if (isCancelButton) {
      setIsCancelButton(false);
      window.location.href = '/publish/settings/authentication/';
    } else {
      setIsDialogOpen(false);
    }
  };

  const onCancel = (isUnsavedChanges) => {
    if (isUnsavedChanges) {
      setIsDialogOpen(true);
      setIsCancelButton(true);
    } else {
      localStorage.setItem('isUnSavedChanges', false);
      window.location.href = `/publish/settings/authentication/edit/${uuid}${qParam}`;
    }
  };

  const onCancelDetails = (isUnsavedChanges) => {
    if (isUnsavedChanges) {
      setIsDialogOpen(true);
      setIsCancelButton(true);
    } else {
      localStorage.setItem('isUnSavedChanges', false);
      window.location.href = '/publish/settings/authentication';
    }
  };

  const handleTabChange = (e, value) => {
    if (currentTab === value) { return; }
      redirectTab(value);
  };

  return (
    <EditContainer
      editPageId="auth-settings-edit-page"
      dialogId="auth-settings-unsaved-dialog"
      isDialogOpen={isDialogOpen}
      onDialogClose={onDialogClose}
      dialogSubmitText={dialogSubmitText}
      onDialogSubmit={onDialogSubmit}
      isLoading={isLoading}
      leftSidebarId="auth-settings-left-sidebar"
      mainContentId="auth-settings-main-content"
      notificationId="auth-settings-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationMessage}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      pathName={pathName}
      tabsContainer={(
        <TabsContainer
          useTabId
          tabValue={currentTab}
          tabItems={tabs}
          handleTabChange={handleTabChange}
        />
      )}
      pageHeaderRef={headerRef}
      pageHeaderTitle={headerTitle}
      pageContent={
        <Fragment>
          {tabs.map(
            ({ tabId, tabPanel }) => (
              <TabPanel
                id={`${tabId}-panel`}
                visible={currentTab === tabId}
                key={tabId}
              >
                {
                  tabPanel({
                    ...props,
                    classes,
                    uuid,
                    authDetails,
                    tabValue: currentTab,
                    errors,
                    onCancel,
                    onCancelDetails,
                    notifyMessages,
                    visible: (currentTab === tabId),
                    onTabChange: handleTabChange,
                    setStateTabs,
                    hasAttributeMappingFields,
                    hasProviderConfigFields,
                    hasDetailsFields,
                    isOktaIdpFlag,
                  })
                }
              </TabPanel>
            ),
          )}
        </Fragment>
      }
      rightSidebarId="auth-settings-right-sidebar"
      helpItems={helpItems}
    />
  );
};

const mapStateToProps = (state) => ({
  userContext: getUserDetails(state),
  authDetails: getAuthSchemeDetails(state),
  isError: getIsError(state),
  authErrors: getErrors(state),
  isSaveSuccess: getIsAuthSaveSuccess(state),
  isUpdateSuccess: getIsAuthUpdateSuccess(state),
  isOktaIdpFlag: getSamlProxied(state),
  isDeleteSuccess: getIsDeleteSuccess(state),
  deleteErrors: getDeleteErrors(state),
  pathName: get(state, 'router.location.pathname'),
});

const mapDispatchToProps = {
  fetchAuthSchemeDetails,
  resetApp,
  push,
  deleteAuth,
};

AuthEdit.propTypes = {
  classes: shape({}),
  userContext: shape({}),
  authDetails: shape({}),
  location: shape({}),
  isError: bool,
  match: shape({}),
  authErrors: arrayOf({}),
  isLoading: bool,
  push: func,
  fetchAuthSchemeDetails: func,
  resetApp: func,
  isSaveSuccess: bool,
  isUpdateSuccess: bool,
  isOktaIdpFlag: bool,
  pathName: string,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(AuthEdit);
